import { shallowEqual } from "react-redux";
import { createSelectorCreator, defaultMemoize } from "reselect";

/**
 * Creates a selector that compares the result of its output selector with
 * `shallowEqual`.
 *
 * This will have the same effect as calling the selector with
 * `useSelector(selector, shallowEqual)`, but bakes in the shallowness of the
 * comparison to the selector itself (instead of relying on the consumer). This
 * can also be used when chaining re-select selectors to make an input
 * selector shallow compared.
 */
export const createShallowResultSelector = createSelectorCreator(
  defaultMemoize,
  { resultEqualityCheck: shallowEqual },
);

/**
 * Creates a selector that compares the result of its input selectors using
 * `shallowEqual`.
 *
 * This will have the same effect as using `createShallowResultSelector`
 * to define all of the input selectors.
 */
export const createShallowInputsSelector = createSelectorCreator(
  defaultMemoize,
  { equalityCheck: shallowEqual },
);

/**
 * Creates a selector that compares the result of both input and output
 * selectors with `shallowEqual`.
 *
 * This is the combination of both `createShallowInputsSelector` and
 * `createShallowResultSelector`.
 */
export const createShallowInputsAndResultSelector = createSelectorCreator(
  defaultMemoize,
  {
    equalityCheck: shallowEqual,
    resultEqualityCheck: shallowEqual,
  },
);
